import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowDownCircle, ArrowUpCircle, CheckCircle2, Download, HandCoins, PlusCircle, Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const entryTypes = [
  { value: 'IN', label: 'Petty Cash In', icon: ArrowUpCircle },
  { value: 'OUT', label: 'Petty Cash Out', icon: ArrowDownCircle },
  { value: 'REIMBURSE', label: 'Reimburse', icon: HandCoins },
];

const pettyCashOutCategories = [
  { value: 'Maintenance', label: 'Maintenance', description: 'Contoh: Cuci AC, Pengecekan CCTV' },
  { value: 'ATK', label: 'ATK', description: 'Contoh: Tissue, Pewangi Ruangan' },
  { value: 'Transportasi', label: 'Transportasi', description: 'Contoh: Bensin, Parkir' },
  { value: 'Pantry/Konsumsi', label: 'Pantry/Konsumsi', description: 'Contoh: Galon' },
  { value: 'Utilitas', label: 'Utilitas', description: 'Contoh: Token Listrik, Wifi' },
  { value: 'Tax', label: 'Tax', description: 'Contoh: Pajak Daerah' },
  { value: 'Entertainment', label: 'Entertainment', description: 'Contoh: Parcel' },
  { value: 'Marketing/Advertising', label: 'Marketing/Advertising', description: 'Contoh: Aktifitas Promosi' },
];

const getPettyCashOutCategoryDescription = (categoryValue) =>
  pettyCashOutCategories.find((category) => category.value === categoryValue)?.description || '';

const normalizeEntryType = (value) => {
  const normalizedType = String(value || '').trim().toUpperCase();
  if (entryTypes.some((item) => item.value === normalizedType)) return normalizedType;
  return 'IN';
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (String(imagePath).startsWith('http://') || String(imagePath).startsWith('https://')) return imagePath;
  return `${BACKEND_URL}${imagePath}`;
};

const isPdfPath = (filePath) => String(filePath || '').trim().toLowerCase().split('?')[0].endsWith('.pdf');

const resolveReimburseSourceLabel = (entry) => {
  const sourceTransactionNumbers = Array.isArray(entry?.reimburse_source_transaction_numbers)
    ? entry.reimburse_source_transaction_numbers.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  if (sourceTransactionNumbers.length > 0) return sourceTransactionNumbers.join(', ');
  return String(entry?.reimburse_source_transaction_number || '').trim();
};

const resolveReimbursedByLabel = (entry) => {
  const transactionNumber = String(entry?.reimbursed_by_transaction_number || '').trim();
  if (transactionNumber) return transactionNumber;
  return String(entry?.reimbursed_by_entry_id || '').trim();
};

const resolveReimburseProofEntries = (entry) => {
  const sourceEntries = Array.isArray(entry?.reimburse_source_entries) ? entry.reimburse_source_entries : [];
  return sourceEntries.filter((sourceEntry) => String(sourceEntry?.photo_path || '').trim());
};

const buildProofPdfFilename = (sourceEntry, index) => {
  const label = String(sourceEntry?.transaction_number || sourceEntry?.id || `bukti-kasir-${index + 1}`)
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '-');
  return `${label || `bukti-kasir-${index + 1}`}.pdf`;
};

export default function PettyCash() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ current_balance: 0, total_in: 0, total_reimburse: 0, total_out: 0, total_entries: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [downloadingReimbursePdfId, setDownloadingReimbursePdfId] = useState('');
  const [approvingEntryId, setApprovingEntryId] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [activeTypeFilter, setActiveTypeFilter] = useState('ALL');
  const [formData, setFormData] = useState({
    entry_type: 'IN',
    amount: '',
    notes: '',
    activity: '',
    category: '',
    entry_date: new Date().toISOString().slice(0, 10),
    photo_path: '',
    reimburse_out_entry_id: '',
    reimburse_out_entry_ids: [],
  });
  const isAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';

  const fetchPettyCash = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/petty-cash`, { withCredentials: true });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setSummary(data?.summary || { current_balance: 0, total_in: 0, total_reimburse: 0, total_out: 0, total_entries: 0 });
    } catch (error) {
      console.error('Failed to fetch petty cash:', error);
      alert(error.response?.data?.detail || 'Gagal mengambil data petty cash.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPettyCash();
  }, []);

  const typeLabelMap = useMemo(
    () => entryTypes.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {}),
    []
  );
  const typeConfigMap = useMemo(
    () => entryTypes.reduce((acc, item) => ({ ...acc, [item.value]: item }), {}),
    []
  );
  const typeStyleMap = {
    IN: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/20',
    OUT: 'bg-red-500/15 text-red-300 ring-1 ring-red-400/20',
    REIMBURSE: 'bg-sky-500/15 text-sky-300 ring-1 ring-sky-400/20',
  };
  const typeCountMap = useMemo(() => {
    const baseCounts = { IN: 0, OUT: 0, REIMBURSE: 0 };
    for (const entry of entries) {
      const entryType = normalizeEntryType(entry.entry_type);
      baseCounts[entryType] = (baseCounts[entryType] || 0) + 1;
    }
    return baseCounts;
  }, [entries]);
  const visibleEntries = useMemo(() => {
    if (activeTypeFilter === 'ALL') return entries;
    return entries.filter((entry) => normalizeEntryType(entry.entry_type) === activeTypeFilter);
  }, [activeTypeFilter, entries]);
  const typeFilterOptions = useMemo(
    () => [
      { value: 'ALL', label: 'Semua', count: entries.length, icon: Wallet },
      ...entryTypes.map((item) => ({
        value: item.value,
        label: item.label,
        count: typeCountMap[item.value] || 0,
        icon: item.icon,
      })),
    ],
    [entries.length, typeCountMap]
  );
  const isOutForm = formData.entry_type === 'OUT';
  const isReimburseForm = formData.entry_type === 'REIMBURSE';

  const reimbursedOutEntryIds = useMemo(() => {
    const ids = new Set();
    for (const entry of entries) {
      if (String(entry.entry_type || '').toUpperCase() !== 'REIMBURSE') continue;
      const sourceIds = Array.isArray(entry.reimburse_out_entry_ids)
        ? entry.reimburse_out_entry_ids
        : [entry.reimburse_out_entry_id];
      for (const sourceId of sourceIds) {
        const normalizedSourceId = String(sourceId || '').trim();
        if (normalizedSourceId) ids.add(normalizedSourceId);
      }
    }
    return ids;
  }, [entries]);

  const availableOutEntries = useMemo(
    () =>
      entries.filter((entry) => {
        if (String(entry.entry_type || '').toUpperCase() !== 'OUT') return false;
        const entryId = String(entry.id || '');
        if (!entryId) return false;
        if (entry.is_reimbursed === true) return false;
        if (reimbursedOutEntryIds.has(entryId)) return false;
        return true;
      }),
    [entries, reimbursedOutEntryIds]
  );

  const selectedReimburseOutEntryIds = useMemo(() => {
    const rawIds = Array.isArray(formData.reimburse_out_entry_ids)
      ? formData.reimburse_out_entry_ids
      : [formData.reimburse_out_entry_id];
    const uniqueIds = [];
    for (const rawId of rawIds) {
      const normalizedId = String(rawId || '').trim();
      if (normalizedId && !uniqueIds.includes(normalizedId)) {
        uniqueIds.push(normalizedId);
      }
    }
    return uniqueIds;
  }, [formData.reimburse_out_entry_id, formData.reimburse_out_entry_ids]);

  const selectedOutEntries = useMemo(() => {
    if (selectedReimburseOutEntryIds.length === 0) return [];
    const selectedIds = new Set(selectedReimburseOutEntryIds);
    return availableOutEntries.filter((entry) => selectedIds.has(String(entry.id || '')));
  }, [availableOutEntries, selectedReimburseOutEntryIds]);

  const selectedOutTotalAmount = useMemo(
    () => selectedOutEntries.reduce((total, entry) => total + Number(entry.amount || 0), 0),
    [selectedOutEntries]
  );
  const selectedOutCategoryDescription = getPettyCashOutCategoryDescription(formData.category);

  const downloadReimbursePdf = async (entry) => {
    const entryId = String(entry?.id || '').trim();
    if (!entryId) return;
    try {
      setDownloadingReimbursePdfId(entryId);
      const response = await axios.get(`${API}/petty-cash/reimburse/export-pdf`, {
        params: { reimburse_entry_id: entryId },
        responseType: 'blob',
        withCredentials: true,
      });
      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const transactionNumber = String(entry.transaction_number || entryId).replace(/[^a-zA-Z0-9_-]/g, '-');
      link.href = url;
      link.download = `reimburse-petty-cash-${transactionNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download reimburse PDF:', error);
      alert(error.response?.data?.detail || 'Gagal download PDF reimburse.');
    } finally {
      setDownloadingReimbursePdfId('');
    }
  };

  useEffect(() => {
    if (!isReimburseForm) return;
    const availableIds = new Set(availableOutEntries.map((entry) => String(entry.id || '')));
    setFormData((prev) => {
      if (prev.entry_type !== 'REIMBURSE') return prev;
      const prevSelectedIds = Array.isArray(prev.reimburse_out_entry_ids)
        ? prev.reimburse_out_entry_ids
        : [prev.reimburse_out_entry_id];
      const nextSelectedIds = prevSelectedIds
        .map((value) => String(value || '').trim())
        .filter((value, index, arr) => value && arr.indexOf(value) === index && availableIds.has(value));
      const nextPrimaryId = nextSelectedIds[0] || '';
      if (
        nextPrimaryId === String(prev.reimburse_out_entry_id || '').trim()
        && nextSelectedIds.length === prevSelectedIds.length
      ) {
        return prev;
      }
      return {
        ...prev,
        reimburse_out_entry_id: nextPrimaryId,
        reimburse_out_entry_ids: nextSelectedIds,
      };
    });
  }, [availableOutEntries, isReimburseForm]);

  const toggleReimburseOutEntry = (entryId) => {
    const normalizedId = String(entryId || '').trim();
    if (!normalizedId) return;
    setFormData((prev) => {
      const currentIds = Array.isArray(prev.reimburse_out_entry_ids)
        ? prev.reimburse_out_entry_ids
            .map((value) => String(value || '').trim())
            .filter((value, index, arr) => value && arr.indexOf(value) === index)
        : [];
      const exists = currentIds.includes(normalizedId);
      const nextIds = exists
        ? currentIds.filter((value) => value !== normalizedId)
        : [...currentIds, normalizedId];
      return {
        ...prev,
        reimburse_out_entry_id: nextIds[0] || '',
        reimburse_out_entry_ids: nextIds,
      };
    });
  };

  const handlePettyCashImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const body = new FormData();
    body.append('file', file);

    try {
      setUploadingImage(true);
      const { data } = await axios.post(`${API}/uploads/petty-cash-image`, body, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((prev) => ({ ...prev, photo_path: data?.path || '' }));
    } catch (error) {
      console.error('Failed to upload petty cash proof:', error);
      alert(error.response?.data?.detail || 'Gagal upload bukti petty cash.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const approveReimburse = async (entry) => {
    const entryId = String(entry?.id || '').trim();
    if (!entryId) return;
    const ok = window.confirm(`Approve reimburse ${entry?.transaction_number || entryId}?`);
    if (!ok) return;

    try {
      setApprovingEntryId(entryId);
      await axios.post(`${API}/petty-cash/entries/${entryId}/approve-reimburse`, {}, { withCredentials: true });
      await fetchPettyCash();
    } catch (error) {
      console.error('Failed to approve reimburse:', error);
      alert(error.response?.data?.detail || 'Gagal approve reimburse.');
    } finally {
      setApprovingEntryId('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const reimburseOutEntryIds = isReimburseForm ? selectedReimburseOutEntryIds : [];
    const amount = isReimburseForm ? Number(selectedOutTotalAmount || 0) : Number(formData.amount || 0);
    const payload = {
      entry_type: formData.entry_type,
      amount,
      notes: formData.notes.trim(),
      activity: formData.activity.trim(),
      category: formData.entry_type === 'OUT' ? formData.category.trim() : '',
      entry_date: formData.entry_date,
      photo_path: formData.entry_type === 'OUT' ? formData.photo_path || '' : '',
      reimburse_out_entry_id: formData.entry_type === 'REIMBURSE' ? reimburseOutEntryIds[0] || '' : '',
      reimburse_out_entry_ids: formData.entry_type === 'REIMBURSE' ? reimburseOutEntryIds : [],
    };

    if (isReimburseForm && payload.reimburse_out_entry_ids.length === 0) {
      alert('Pilih transaksi Petty Cash Out yang mau direimburse.');
      return;
    }

    if (isReimburseForm && selectedOutEntries.length !== payload.reimburse_out_entry_ids.length) {
      alert('Ada transaksi Petty Cash Out yang dipilih sudah tidak tersedia. Silakan pilih ulang.');
      return;
    }

    if (isOutForm && !payload.activity) {
      alert('Detail pengeluaran untuk Petty Cash Out wajib diisi.');
      return;
    }

    if (isOutForm && !payload.category) {
      alert('Kategori Petty Cash Out wajib dipilih.');
      return;
    }

    if (isOutForm && !payload.photo_path) {
      alert('Petty Cash Out wajib upload foto nota atau PDF bukti.');
      return;
    }

    if (!payload.amount || payload.amount <= 0) {
      alert('Nominal harus lebih dari 0.');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/petty-cash/entries`, payload, { withCredentials: true });
      setFormData({
        entry_type: 'IN',
        amount: '',
        notes: '',
        activity: '',
        category: '',
        entry_date: new Date().toISOString().slice(0, 10),
        photo_path: '',
        reimburse_out_entry_id: '',
        reimburse_out_entry_ids: [],
      });
      setIsFormModalOpen(false);
      await fetchPettyCash();
    } catch (error) {
      console.error('Failed to create petty cash entry:', error);
      alert(error.response?.data?.detail || 'Gagal menyimpan transaksi petty cash.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <Header title="Petty Cash" />
      <div className="space-y-5 p-6 lg:p-8" data-testid="petty-cash-page">
        <div className="flex flex-col gap-4 rounded-sm border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.15),rgba(9,9,11,0)_42%),linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))] p-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Badge className="mb-3 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/10">
              Kasir
            </Badge>
            <h2 className="text-2xl font-semibold tracking-tight text-white">Petty Cash</h2>
            <p className="mt-1 text-sm text-zinc-400">Kelola kas masuk, kas keluar, dan reimburse operasional kasir.</p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={fetchPettyCash}
              className="rounded-sm border-white/20 bg-white/[0.03] text-white hover:bg-white/[0.08]"
            >
              Refresh Data
            </Button>
            <Button
              type="button"
              onClick={() => {
                setFormData({
                  entry_type: 'IN',
                  amount: '',
                  notes: '',
                  activity: '',
                  category: '',
                  entry_date: new Date().toISOString().slice(0, 10),
                  photo_path: '',
                  reimburse_out_entry_id: '',
                  reimburse_out_entry_ids: [],
                });
                setIsFormModalOpen(true);
              }}
              className="rounded-sm bg-[#D4AF37] text-black hover:bg-[#C59A27]"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Catat Petty Cash
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-sm border border-[#D4AF37]/25 bg-[linear-gradient(135deg,rgba(212,175,55,0.12),rgba(9,9,11,0.84))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Saldo Saat Ini</p>
            <p className="mt-3 text-2xl font-semibold text-[#D4AF37]">{formatCurrency(summary.current_balance)}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total In</p>
            <p className="mt-3 text-2xl font-semibold text-emerald-300">{formatCurrency(summary.total_in)}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total Reimburse</p>
            <p className="mt-3 text-2xl font-semibold text-sky-300">{formatCurrency(summary.total_reimburse)}</p>
            {Number(summary.total_reimburse_pending || 0) > 0 ? (
              <p className="mt-1 text-xs text-amber-300">Pending: {formatCurrency(summary.total_reimburse_pending)}</p>
            ) : null}
          </div>
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total Out</p>
            <p className="mt-3 text-2xl font-semibold text-red-300">{formatCurrency(summary.total_out)}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total Transaksi</p>
            <p className="mt-3 text-2xl font-semibold text-white">{summary.total_entries || 0}</p>
          </div>
        </div>

        <div className="rounded-sm border border-white/10 bg-zinc-950/45 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Filter Tipe Transaksi</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {typeFilterOptions.map((option) => {
              const isActive = activeTypeFilter === option.value;
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setActiveTypeFilter(option.value)}
                  data-testid={`petty-cash-type-filter-${option.value.toLowerCase()}`}
                  className={`inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    isActive
                      ? 'border-[#D4AF37]/45 bg-[#D4AF37]/15 text-[#F3D77A]'
                      : 'border-white/15 bg-white/[0.02] text-zinc-300 hover:border-[#D4AF37]/30 hover:text-[#F3D77A]'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{option.label}</span>
                  <span className="rounded-sm border border-current/25 px-1.5 py-0.5 text-[10px] leading-none">{option.count}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-zinc-500">
            Menampilkan {visibleEntries.length} dari {entries.length} transaksi.
          </p>
        </div>

        <div className="overflow-hidden rounded-sm border border-white/10 bg-zinc-950/45 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
          <div className="grid grid-cols-[1.35fr_.95fr_.9fr_1fr_1.4fr] gap-4 border-b border-white/10 bg-white/[0.02] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              <span>No. Transaksi</span>
              <span>Tipe</span>
              <span>Nominal</span>
              <span>Saldo Akhir</span>
              <span>Detail</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
            </div>
          ) : visibleEntries.length > 0 ? (
            <div className="max-h-[560px] divide-y divide-white/5 overflow-auto">
              {visibleEntries.map((entry) => {
                const entryType = normalizeEntryType(entry.entry_type);
                const reimburseSourceLabel = resolveReimburseSourceLabel(entry);
                const reimbursedByLabel = resolveReimbursedByLabel(entry);
                const isOutflow = entryType === 'OUT' || entryType === 'REIMBURSE';
                const typeConfig = typeConfigMap[entryType];
                const TypeIcon = typeConfig?.icon;
                const approvalStatus = entryType === 'REIMBURSE'
                  ? String(entry.approval_status || 'APPROVED').toUpperCase()
                  : '';
                const isPendingReimburse = entryType === 'REIMBURSE' && approvalStatus === 'PENDING';
                const isRejectedReimburse = entryType === 'REIMBURSE' && approvalStatus === 'REJECTED';
                const isApprovedReimburse = entryType === 'REIMBURSE' && approvalStatus === 'APPROVED';
                const reimburseProofEntries = entryType === 'REIMBURSE' ? resolveReimburseProofEntries(entry) : [];
                const cashierPdfProofEntries = reimburseProofEntries.filter((sourceEntry) => isPdfPath(sourceEntry.photo_path));
                return (
                  <div key={entry.id} className="grid grid-cols-[1.35fr_.95fr_.9fr_1fr_1.4fr] gap-4 px-5 py-3.5 text-sm transition-colors hover:bg-white/[0.02]">
                    <div className="min-w-0 space-y-1">
                      <p className="truncate font-mono text-[13px] font-semibold tracking-[0.02em] text-zinc-100">
                        {entry.transaction_number || '-'}
                      </p>
                      {entryType === 'REIMBURSE' && reimburseSourceLabel ? (
                        <p className="truncate text-xs text-sky-300/90">Sumber Out: {reimburseSourceLabel}</p>
                      ) : null}
                      {entryType === 'OUT' ? (
                        entry.is_reimbursed ? (
                          <p className="truncate text-xs text-sky-300">
                            Terhubung Reimburse: {reimbursedByLabel || '-'}
                          </p>
                        ) : entry.reimburse_approval_status === 'PENDING' ? (
                          <p className="truncate text-xs text-amber-300">
                            Menunggu approval: {entry.pending_reimburse_transaction_number || '-'}
                          </p>
                        ) : (
                          <p className="text-xs text-zinc-600">Belum direimburse</p>
                        )
                      ) : null}
                    </div>
                    <div className="pt-0.5">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${typeStyleMap[entryType] || 'bg-white/10 text-zinc-200'}`}>
                        {TypeIcon ? <TypeIcon className="h-3.5 w-3.5" /> : null}
                        {typeLabelMap[entryType] || entryType}
                      </span>
                    </div>
                    <p className={`pt-0.5 text-right font-semibold tabular-nums ${isOutflow ? 'text-red-300' : 'text-emerald-300'}`}>
                      {isOutflow ? '-' : '+'}{formatCurrency(entry.amount)}
                    </p>
                    <p className="pt-0.5 text-right font-semibold text-[#F3D77A] tabular-nums">{formatCurrency(entry.balance_after)}</p>
                    <div className="min-w-0 space-y-1">
                      <p className="font-medium text-zinc-200 tabular-nums">{formatDateTime(entry.created_at)}</p>
                      {entry.category ? <p className="truncate text-xs text-[#F3D77A]">Kategori: {entry.category}</p> : null}
                      {entry.activity ? <p className="truncate text-xs text-zinc-400">Detail: {entry.activity}</p> : <p className="text-xs text-zinc-600">-</p>}
                      {entry.notes ? <p className="truncate text-xs text-zinc-500">{entry.notes}</p> : null}
                      {entryType === 'REIMBURSE' ? (
                        <span className={`inline-flex rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${
                          isPendingReimburse
                            ? 'border border-amber-500/30 bg-amber-500/10 text-amber-300'
                            : isRejectedReimburse
                            ? 'border border-red-500/30 bg-red-500/10 text-red-300'
                            : 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                        }`}>
                          {isPendingReimburse ? 'Menunggu Approval' : isRejectedReimburse ? 'Rejected' : 'Approved'}
                        </span>
                      ) : null}
                      {isApprovedReimburse ? (
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => downloadReimbursePdf(entry)}
                            disabled={downloadingReimbursePdfId === entry.id}
                            className="inline-flex items-center rounded-sm border border-sky-400/30 bg-sky-500/10 px-2 py-1 text-[11px] font-semibold text-sky-200 transition-colors hover:bg-sky-500/15 disabled:cursor-wait disabled:opacity-60"
                            title="Download PDF reimburse"
                          >
                            <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.8} />
                            {downloadingReimbursePdfId === entry.id ? 'Download...' : 'Download PDF'}
                          </button>
                          {cashierPdfProofEntries.map((sourceEntry, proofIndex) => {
                            const sourceLabel = sourceEntry.transaction_number || `Bukti ${proofIndex + 1}`;
                            return (
                              <a
                                key={`${entry.id}-pdf-${sourceEntry.id || proofIndex}`}
                                href={resolveImageUrl(sourceEntry.photo_path)}
                                download={buildProofPdfFilename(sourceEntry, proofIndex)}
                                className="inline-flex items-center rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-2 py-1 text-[11px] font-semibold text-[#F3D77A] transition-colors hover:bg-[#D4AF37]/15"
                                title={`Download PDF kasir: ${sourceLabel}`}
                              >
                                <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.8} />
                                {cashierPdfProofEntries.length > 1 ? `PDF Kasir ${proofIndex + 1}` : 'PDF Kasir'}
                              </a>
                            );
                          })}
                        </div>
                      ) : null}
                      {entry.photo_path && !isPdfPath(entry.photo_path) ? (
                        <a
                          href={resolveImageUrl(entry.photo_path)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center rounded-sm text-xs font-medium text-sky-300 transition-colors hover:text-sky-200"
                        >
                          Lihat Foto
                        </a>
                      ) : null}
                      {entryType === 'REIMBURSE' && reimburseProofEntries.some((sourceEntry) => !isPdfPath(sourceEntry.photo_path)) ? (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {reimburseProofEntries.filter((sourceEntry) => !isPdfPath(sourceEntry.photo_path)).map((sourceEntry, proofIndex) => {
                            const sourceLabel = sourceEntry.transaction_number || `Bukti ${proofIndex + 1}`;
                            return (
                              <a
                                key={`${entry.id}-${sourceEntry.id || proofIndex}`}
                                href={resolveImageUrl(sourceEntry.photo_path)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-2 py-1 text-[11px] font-semibold text-[#F3D77A] transition-colors hover:bg-[#D4AF37]/15"
                                title={sourceLabel}
                              >
                                Lihat Foto Kasir
                              </a>
                            );
                          })}
                        </div>
                      ) : null}
                      {isPendingReimburse && isAdmin ? (
                        <button
                          type="button"
                          onClick={() => approveReimburse(entry)}
                          disabled={approvingEntryId === entry.id}
                          className="mt-1 inline-flex items-center gap-1 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/15 disabled:cursor-wait disabled:opacity-60"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.7} />
                          {approvingEntryId === entry.id ? 'Approve...' : 'Approve'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Wallet className="mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
              <p className="text-sm font-medium text-white">
                {entries.length > 0 ? 'Tidak ada transaksi untuk tipe yang dipilih' : 'Belum ada transaksi petty cash'}
              </p>
              <p className="mt-1 text-sm text-zinc-500">
                {entries.length > 0
                  ? 'Ubah filter tipe untuk melihat transaksi lainnya.'
                  : 'Klik tombol Catat Petty Cash untuk menambah transaksi baru.'}
              </p>
            </div>
          )}
        </div>

        <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
          <DialogContent className="flex max-h-[88vh] max-w-md flex-col overflow-hidden rounded-sm border-white/10 bg-zinc-900 p-0 text-white shadow-[0_18px_70px_rgba(0,0,0,0.55)]">
            <DialogHeader>
              <DialogTitle className="px-6 pt-6 text-lg">Input Transaksi Petty Cash</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Jenis Transaksi</Label>
                  <select
                    value={formData.entry_type}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        entry_type: e.target.value,
                        amount: e.target.value === 'REIMBURSE' ? prev.amount : '',
                        activity: e.target.value === 'OUT' ? prev.activity : '',
                        category: e.target.value === 'OUT' ? prev.category : '',
                        photo_path: e.target.value === 'OUT' ? prev.photo_path : '',
                        reimburse_out_entry_id: e.target.value === 'REIMBURSE' ? prev.reimburse_out_entry_id : '',
                        reimburse_out_entry_ids: e.target.value === 'REIMBURSE' ? prev.reimburse_out_entry_ids : [],
                      }))
                    }
                    className="h-10 w-full rounded-sm border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none focus:border-[#D4AF37] [&_option]:bg-white [&_option]:text-black"
                  >
                    {entryTypes.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </div>
                {isReimburseForm ? (
                  <>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Pilih Petty Cash Out</Label>
                    {availableOutEntries.length > 0 ? (
                      <div className="max-h-52 space-y-2 overflow-auto rounded-sm border border-white/10 bg-zinc-950/70 p-2">
                        {availableOutEntries.map((entry) => {
                          const isChecked = selectedReimburseOutEntryIds.includes(entry.id);
                          return (
                            <label
                              key={entry.id}
                              className="flex cursor-pointer items-start gap-3 rounded-sm border border-white/10 bg-white/[0.02] px-2.5 py-2 text-sm text-zinc-200 transition-colors hover:bg-white/[0.05]"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleReimburseOutEntry(entry.id)}
                                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-zinc-900 text-[#D4AF37] accent-[#D4AF37]"
                              />
                              <span className="min-w-0">
                                <span className="block truncate font-medium text-white">{entry.transaction_number}</span>
                                <span className="block truncate text-xs text-zinc-400">
                                  {formatCurrency(entry.amount)} | {entry.activity || 'Tanpa kegiatan'}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-500">Semua Petty Cash Out sudah direimburse atau belum ada data.</p>
                    )}
                    {availableOutEntries.length > 0 ? (
                      <p className="text-xs text-zinc-500">
                        Dipilih: {selectedReimburseOutEntryIds.length} transaksi
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nominal Reimburse</Label>
                    <Input
                      type="text"
                      value={selectedOutEntries.length > 0 ? formatCurrency(selectedOutTotalAmount) : '-'}
                      className="rounded-sm border-white/10 bg-zinc-950 text-zinc-300"
                      readOnly
                    />
                  </div>
                  )}
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nominal</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.amount}
                      onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
                      className="rounded-sm border-white/10 bg-zinc-950 text-white"
                      placeholder="Contoh: 50000"
                      required
                    />
                  </div>
                )}
                {isOutForm && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Kategori</Label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                        className="h-10 w-full rounded-sm border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none focus:border-[#D4AF37] [&_option]:bg-white [&_option]:text-black"
                        required
                      >
                        <option value="">Pilih kategori</option>
                        {pettyCashOutCategories.map((category) => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                      {selectedOutCategoryDescription ? (
                        <p className="rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/5 px-3 py-2 text-xs leading-relaxed text-[#F3D77A]">
                          {selectedOutCategoryDescription}
                        </p>
                      ) : (
                        <p className="text-xs text-zinc-500">Pilih kategori untuk melihat deskripsi/contoh pengeluaran.</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Detail Pengeluaran</Label>
                      <Input
                        type="text"
                        value={formData.activity}
                        onChange={(e) => setFormData((prev) => ({ ...prev, activity: e.target.value }))}
                        className="rounded-sm border-white/10 bg-zinc-950 text-white"
                        placeholder={selectedOutCategoryDescription || 'Contoh: Cuci AC'}
                        required
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Tanggal</Label>
                  <Input
                    type="date"
                    value={formData.entry_date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, entry_date: e.target.value }))}
                    className="rounded-sm border-white/10 bg-zinc-950 text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Catatan</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                    className="min-h-24 rounded-sm border-white/10 bg-zinc-950 text-white"
                    placeholder="Opsional"
                  />
                </div>
                {isOutForm && (
                  <div className="space-y-3 rounded-sm border border-white/10 bg-zinc-950/60 p-3">
                    <div className="space-y-1">
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
                        Bukti Petty Cash Out (Wajib)
                      </Label>
                      <p className="text-xs leading-relaxed text-zinc-500">
                        Upload foto nota atau PDF bukti dari kasir.
                      </p>
                    </div>
                    <Input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handlePettyCashImageUpload}
                      className="h-11 cursor-pointer rounded-sm border-white/10 bg-black/30 text-sm text-zinc-300 file:mr-3 file:h-full file:cursor-pointer file:border-0 file:bg-[#D4AF37] file:px-4 file:text-sm file:font-semibold file:text-black hover:border-white/20"
                      data-testid="petty-cash-image-input"
                    />
                    {uploadingImage && <p className="text-xs text-zinc-500">Mengupload bukti...</p>}
                    {formData.photo_path ? (
                      <div className="rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#F3D77A]">
                              Bukti terlampir
                            </p>
                            <p className="mt-1 truncate text-xs text-zinc-400">
                              {isPdfPath(formData.photo_path) ? 'PDF Bukti' : 'Foto Bukti'}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <a
                              href={resolveImageUrl(formData.photo_path)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-2 text-xs font-semibold text-[#F3D77A] transition-colors hover:bg-[#D4AF37]/15"
                            >
                              {isPdfPath(formData.photo_path) ? 'Lihat PDF' : 'Lihat Foto'}
                            </a>
                            <button
                              type="button"
                              onClick={() => setFormData((prev) => ({ ...prev, photo_path: '' }))}
                              className="inline-flex rounded-sm border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-300 transition-colors hover:bg-rose-500/15"
                            >
                              Hapus
                            </button>
                          </div>
                        </div>
                        {!isPdfPath(formData.photo_path) ? (
                          <img
                            src={resolveImageUrl(formData.photo_path)}
                            alt="Bukti petty cash"
                            className="mt-3 max-h-44 w-auto rounded-sm border border-white/10 object-cover"
                          />
                        ) : null}
                      </div>
                    ) : (
                      <p className="rounded-sm border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
                        Belum ada bukti. Upload foto nota atau PDF bukti.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="border-t border-white/10 bg-zinc-900 px-6 py-4">
                <Button type="submit" disabled={submitting || uploadingImage || (isOutForm && !formData.photo_path)} className="w-full rounded-sm bg-[#D4AF37] text-black hover:bg-[#C59A27] disabled:cursor-not-allowed disabled:opacity-50">
                  {submitting ? 'Menyimpan...' : 'Simpan Transaksi'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}
